// note: general import
import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

// note: state import
import { selectAuth, authAction } from "state/reducer/auth";

// note MUI import
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid2";
import { IconButton, InputAdornment, Paper, TextField } from "@mui/material";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { LockOutlined, Visibility, VisibilityOff } from "@mui/icons-material";

// note: utils import
import { BimaLogoNew, TenantManagement } from "utils/asset";

import { LazyLoadImage } from "react-lazy-load-image-component";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

// note: password must match backend rules (utils/sanitizer.js -> sanitizerPassword)
function validatePassword(password) {
  if (password.length < 6) return "At least 6 characters";
  if (!/[A-Z]/.test(password)) return "At least one uppercase letter";
  if (!/\d/.test(password)) return "At least one number";
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password))
    return "At least one symbol";
  return "";
}

function ResetPasswordPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: otp } = useParams();

  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  const resetPassword = useSelector(selectAuth)?.resetPassword;

  const passwordError = password.length > 0 ? validatePassword(password) : "";
  const confirmError =
    confirmPassword.length > 0 && confirmPassword !== password
      ? "Passwords do not match"
      : "";

  function handleResetPasswordSubmit(event) {
    event.preventDefault();

    dispatch(
      authAction.resetPasswordThunk({
        pathname: "/reset-password",
        queryParams: `?otp=${otp}`,
        body: { password },
      }),
    );
  }

  function handleBackToLogin() {
    dispatch(authAction.setClearResetPassword());
    dispatch(authAction.setClearResetPasswordInput());
    navigate("/login", { replace: true });
  }

  return (
    <Paper
      elevation={0}
      sx={{
        minHeight: "100vh",
        height: "100%",
        borderRadius: 0,
        overflow: "hidden",
        fontFamily: "Manrope, sans-serif",
        p: 2,
      }}
    >
      <Grid container sx={{ height: "100vh" }}>
        {/* --- KOLOM KIRI (FORMULIR) --- */}
        <Grid
          size={{ xs: 12, md: 4.5 }}
          sx={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#ffffff",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              p: { xs: 3, sm: 5, md: 6 },
              flexGrow: 1,
              overflowY: "auto",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <LazyLoadImage
                alt="logo"
                height="42px"
                src={BimaLogoNew}
                width="42px"
                style={{ padding: 1 }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, color: "#1a202c" }}
              >
                BIMA TENANT
              </Typography>
            </Box>

            {resetPassword?.isFulfilled ? (
              <>
                <TaskAltOutlinedIcon
                  sx={{ fontSize: 48, color: "#1a202c", mt: 3 }}
                />
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, color: "#1a202c", mt: 2, mb: 1 }}
                >
                  Password updated
                </Typography>
                <Typography variant="body1" sx={{ color: "#718096", mb: 4 }}>
                  Your password has been reset successfully. You can now sign in
                  with your new password.
                </Typography>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleBackToLogin}
                  sx={{
                    borderRadius: "12px",
                    textTransform: "none",
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                    width: "fit-content",
                  }}
                >
                  Go to Login
                </Button>
              </>
            ) : (
              <>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, color: "#1a202c", mt: 3, mb: 1 }}
                >
                  Set new password
                </Typography>
                <Typography variant="body1" sx={{ color: "#718096", mb: 4 }}>
                  Your new password must be different from previously used
                  passwords.
                </Typography>

                <Box
                  component="form"
                  noValidate
                  onSubmit={handleResetPasswordSubmit}
                >
                  <Typography
                    variant="body2"
                    align="start"
                    sx={{ mt: 2, color: "#718096" }}
                  >
                    New Password
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    error={Boolean(passwordError)}
                    helperText={passwordError || " "}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined
                            sx={{ color: "#a0aec0", fontSize: "18px" }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword((show) => !show)}
                            onMouseDown={(event) => event.preventDefault()}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityOff sx={{ fontSize: "18px" }} />
                            ) : (
                              <Visibility sx={{ fontSize: "18px" }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "#f7fafc",
                        "& fieldset": { borderColor: "#e2e8f0" },
                      },
                    }}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />

                  <Typography
                    variant="body2"
                    align="start"
                    sx={{ mt: 1, color: "#718096" }}
                  >
                    Confirm Password
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    name="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Re-enter new password"
                    error={Boolean(confirmError)}
                    helperText={confirmError || " "}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined
                            sx={{ color: "#a0aec0", fontSize: "18px" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "#f7fafc",
                        "& fieldset": { borderColor: "#e2e8f0" },
                      },
                    }}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                  />

                  {resetPassword?.isRejected && (
                    <Typography
                      variant="body2"
                      sx={{ color: "#e53e3e", mt: 1 }}
                    >
                      {resetPassword?.err || "Failed to reset password"}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={
                      !password ||
                      !confirmPassword ||
                      Boolean(validatePassword(password)) ||
                      password !== confirmPassword ||
                      resetPassword?.isPending
                    }
                    sx={{
                      mt: 2,
                      mb: 2,
                      py: 1.5,
                      borderRadius: "12px",
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      boxShadow: "0 4px 14px 0 rgba(9, 255, 0, 0.39)",
                      "&:hover": { bgcolor: "#a5f5c2", color: "#166534" },
                    }}
                  >
                    {resetPassword?.isPending ? (
                      <CircularProgress size={22} sx={{ color: "#1a202c" }} />
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Grid>

        {/* --- KOLOM KANAN (PROMO) --- */}
        <Grid
          size={{ xs: 12, md: 7.5 }}
          sx={{
            position: "relative",
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "space-between",
            p: 6,
            color: "#ffffff",
            height: { md: "100%" },
          }}
        >
          <DotLottieReact src={TenantManagement} loop autoplay />
        </Grid>
      </Grid>
    </Paper>
  );
}

export default ResetPasswordPage;
